/// <reference types="vite/client" />
import { useEffect, useRef, useState } from "react";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BookOpen, Calendar, CalendarClock, ChevronDown, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { useClickOutside } from "../../hooks";
import type { UserWorkspace } from "../../types/reports";

interface Tab {
  id: string;
  name: string;
  isScheduled?: boolean;
}

interface DashboardTabsProps {
  // Workspace
  workspaces: UserWorkspace[];
  activeWorkspace: string;
  hasScheduledDashboards: boolean;
  onWorkspaceSelect: (id: string) => void;
  onWorkspaceCreate: () => void;
  onWorkspaceRename: (id: string, name: string) => void;
  onWorkspaceDelete: (id: string) => void;
  onUnassignedRename: (name: string) => void;
  // Library
  onLibraryOpen: () => void;
  // Scheduled dashboards (analyst+)
  canSchedule?: boolean;
  onScheduleOpen?: () => void;
  // Tabs
  tabs: Tab[];
  activeTab: string | null;
  onTabSelect: (id: string) => void;
  onTabCreate: () => void;
  onTabRename: (id: string, name: string) => void;
  onTabDelete: (id: string) => void;
  onTabReorder: (ids: string[]) => void;
  onTabMove: (id: string, isScheduled: boolean) => void;
  renamingId: string | null;
  renameValue: string;
  onRenameValueChange: (v: string) => void;
  onRenameStart: (id: string, name: string) => void;
  onRenameCancel: () => void;
}

export default function DashboardTabs({
  workspaces,
  activeWorkspace,
  hasScheduledDashboards,
  onWorkspaceSelect,
  onWorkspaceCreate,
  onWorkspaceRename,
  onWorkspaceDelete,
  onUnassignedRename,
  onLibraryOpen,
  canSchedule = false,
  onScheduleOpen,
  tabs,
  activeTab,
  onTabSelect,
  onTabCreate,
  onTabRename,
  onTabDelete,
  onTabReorder,
  onTabMove,
  renamingId,
  renameValue,
  onRenameValueChange,
  onRenameStart,
  onRenameCancel,
}: DashboardTabsProps) {
  const tabSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [tabDropdownOpen, setTabDropdownOpen] = useState(false);
  const tabDropdownRef = useRef<HTMLDivElement>(null);

  useClickOutside(tabDropdownRef, () => setTabDropdownOpen(false), tabDropdownOpen);

  function handleTabDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const ids = tabs.map((t) => t.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onTabReorder(arrayMove(ids, oldIndex, newIndex));
  }

  const showWorkspaceSelector = workspaces.length > 0 || hasScheduledDashboards;
  const activeTabLabel = tabs.find((t) => t.id === activeTab)?.name ?? "Dashboard";

  return (
    <div className="shrink-0 border-b border-gray-200 bg-white">

      {/* ── Mobile layout ── */}
      <div className="flex md:hidden items-center gap-2 px-3 py-2">
        {showWorkspaceSelector && (
          <WorkspaceSelector
            workspaces={workspaces}
            activeWorkspace={activeWorkspace}
            hasScheduledDashboards={hasScheduledDashboards}
            onSelect={onWorkspaceSelect}
            onCreate={onWorkspaceCreate}
            onRename={onWorkspaceRename}
            onDelete={onWorkspaceDelete}
            onUnassignedRename={onUnassignedRename}
          />
        )}

        {/* Tab dropdown */}
        <div ref={tabDropdownRef} className="relative min-w-0 flex-1">
          <button
            onClick={() => setTabDropdownOpen((o) => !o)}
            className="flex w-full items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-gray-300"
          >
            <span className="min-w-0 flex-1 truncate text-left">{activeTabLabel}</span>
            <ChevronDown size={12} className={`shrink-0 transition-transform ${tabDropdownOpen ? "rotate-180" : ""}`} />
          </button>
          {tabDropdownOpen && (
            <div
              style={{ position: "fixed", top: (tabDropdownRef.current?.getBoundingClientRect().bottom ?? 0) + 4, left: (tabDropdownRef.current?.getBoundingClientRect().left ?? 0), width: tabDropdownRef.current?.getBoundingClientRect().width ?? 200, zIndex: 50 }}
              className="rounded-xl border border-gray-200 bg-white shadow-lg"
            >
              <div className="max-h-52 overflow-y-auto py-1">
                {tabs.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-gray-400">No dashboards yet</p>
                ) : (
                  tabs.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => { onTabSelect(t.id); setTabDropdownOpen(false); }}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-gray-50 ${activeTab === t.id ? "font-semibold text-[#1D345F]" : "text-gray-700"}`}
                    >
                      {t.isScheduled && <Calendar size={11} className="shrink-0 opacity-60" />}
                      {t.name}
                    </button>
                  ))
                )}
              </div>
              {activeWorkspace !== "__scheduled__" && (
                <div className="border-t border-gray-100 py-1">
                  <button
                    onClick={() => { setTabDropdownOpen(false); onTabCreate(); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[#1D345F] hover:bg-gray-50"
                  >
                    <Plus size={12} />
                    New dashboard
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Schedule + Library icon buttons */}
        {canSchedule && onScheduleOpen && (
          <button
            onClick={onScheduleOpen}
            title="Schedule a dashboard"
            className="shrink-0 flex items-center rounded-lg border border-gray-200 p-1.5 text-gray-600 hover:border-gray-300 hover:text-gray-800"
          >
            <CalendarClock size={14} />
          </button>
        )}
        <button
          onClick={onLibraryOpen}
          title="Library"
          className="shrink-0 flex items-center rounded-lg border border-gray-200 p-1.5 text-gray-600 hover:border-gray-300 hover:text-gray-800"
        >
          <BookOpen size={14} />
        </button>
      </div>

      {/* ── Desktop layout ── */}
      <div className="hidden md:flex items-center px-4">

        {/* Fixed left: workspace selector */}
        {showWorkspaceSelector && (
          <>
            <div className="shrink-0 py-1">
              <WorkspaceSelector
                workspaces={workspaces}
                activeWorkspace={activeWorkspace}
                hasScheduledDashboards={hasScheduledDashboards}
                onSelect={onWorkspaceSelect}
                onCreate={onWorkspaceCreate}
                onRename={onWorkspaceRename}
                onDelete={onWorkspaceDelete}
                onUnassignedRename={onUnassignedRename}
              />
            </div>
            <div className="mx-3 h-5 w-px shrink-0 bg-gray-200" />
          </>
        )}

        {/* Scrollable middle: tabs + new-tab button */}
        <div className="flex min-w-0 flex-1 items-center overflow-x-auto">
          <DndContext sensors={tabSensors} collisionDetection={closestCenter} onDragEnd={handleTabDragEnd}>
            <SortableContext items={tabs.map((t) => t.id)} strategy={horizontalListSortingStrategy}>
              {tabs.map((t) => (
                <SortableTab
                  key={t.id}
                  tab={t}
                  active={activeTab === t.id}
                  isRenaming={renamingId === t.id}
                  renameValue={renameValue}
                  onSelect={() => onTabSelect(t.id)}
                  onRenameChange={onRenameValueChange}
                  onCommitRename={() => onTabRename(t.id, renameValue.trim())}
                  onCancelRename={onRenameCancel}
                  onStartRename={() => onRenameStart(t.id, t.name)}
                  onDelete={() => onTabDelete(t.id)}
                  onMove={() => onTabMove(t.id, t.isScheduled ?? false)}
                />
              ))}
            </SortableContext>
          </DndContext>

          {activeWorkspace !== "__scheduled__" && (
            tabs.length === 0 ? (
              <button
                onClick={onTabCreate}
                className="flex shrink-0 items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-colors"
              >
                <Plus size={13} />
                Create your first dashboard
              </button>
            ) : (
              <button
                onClick={onTabCreate}
                className="ml-1 flex shrink-0 items-center gap-1 rounded-lg px-3 py-2.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                title="New dashboard"
              >
                <Plus size={15} />
              </button>
            )
          )}
        </div>

        {/* Fixed right: schedule + library buttons */}
        <div className="ml-2 flex shrink-0 items-center gap-2 border-l border-gray-100 pl-3 py-2">
          {canSchedule && onScheduleOpen && (
            <button
              onClick={onScheduleOpen}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-gray-300 hover:text-gray-800"
            >
              <CalendarClock size={13} />
              Schedule a dashboard
            </button>
          )}
          <button
            onClick={onLibraryOpen}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-gray-300 hover:text-gray-800"
          >
            <BookOpen size={13} />
            Library
          </button>
        </div>

      </div>
    </div>
  );
}

// ── Workspace selector ─────────────────────────────────────────────────────────

interface WorkspaceSelectorProps {
  workspaces: UserWorkspace[];
  activeWorkspace: string;
  hasScheduledDashboards: boolean;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onUnassignedRename: (name: string) => void;
}

function WorkspaceSelector({
  workspaces,
  activeWorkspace,
  hasScheduledDashboards,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  onUnassignedRename,
}: WorkspaceSelectorProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editMenuOpen, setEditMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");

  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const renameRef = useRef<HTMLInputElement>(null);
  const editBtnRef = useRef<HTMLButtonElement>(null);
  const editMenuRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => { setDropdownOpen(false); setSearch(""); }, dropdownOpen);

  useEffect(() => {
    if (!editMenuOpen) return;
    function handle(e: MouseEvent) {
      if (
        !editBtnRef.current?.contains(e.target as Node) &&
        !editMenuRef.current?.contains(e.target as Node)
      ) {
        setEditMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [editMenuOpen]);

  useEffect(() => {
    if (dropdownOpen) setTimeout(() => searchRef.current?.focus(), 50);
  }, [dropdownOpen]);

  useEffect(() => {
    if (renaming) setTimeout(() => renameRef.current?.select(), 50);
  }, [renaming]);

  function currentLabel() {
    if (activeWorkspace === "__scheduled__") return "Scheduled";
    if (activeWorkspace === "__unassigned__") return "Unassigned";
    return workspaces.find((ws) => ws.id === activeWorkspace)?.name ?? "Workspace";
  }

  const isVirtual = activeWorkspace === "__scheduled__" || activeWorkspace === "__unassigned__";
  const isScheduledActive = activeWorkspace === "__scheduled__";

  const allOptions = [
    ...workspaces.map((ws) => ({ id: ws.id, label: ws.name, virtual: false })),
    ...(workspaces.length > 0 ? [{ id: "__unassigned__", label: "Unassigned", virtual: true }] : []),
    ...(hasScheduledDashboards ? [{ id: "__scheduled__", label: "Scheduled", virtual: true }] : []),
  ];

  const filtered = search.trim()
    ? allOptions.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : allOptions;

  function commitRename() {
    const name = renameValue.trim();
    setRenaming(false);
    if (!name) return;
    if (activeWorkspace === "__unassigned__") {
      if (name !== "Unassigned") onUnassignedRename(name);
    } else {
      const ws = workspaces.find((w) => w.id === activeWorkspace);
      if (ws && name !== ws.name) onRename(activeWorkspace, name);
    }
  }

  function startRename() {
    setEditMenuOpen(false);
    const current = activeWorkspace === "__unassigned__"
      ? "Unassigned"
      : workspaces.find((w) => w.id === activeWorkspace)?.name ?? "";
    setRenameValue(current);
    setRenaming(true);
  }

  if (workspaces.length === 0 && !hasScheduledDashboards) return null;

  // eslint-disable-next-line react-hooks/refs -- read for fixed-dropdown positioning
  const anchorRect = containerRef.current?.getBoundingClientRect();

  return (
    <div className="flex items-center gap-1" ref={containerRef}>
      {renaming ? (
        <input
          ref={renameRef}
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
            if (e.key === "Escape") setRenaming(false);
          }}
          maxLength={80}
          className="w-36 rounded-lg border border-[#1D345F] bg-white px-2.5 py-1.5 text-xs font-medium text-gray-900 focus:outline-none focus:ring-1 focus:ring-[#1D345F]"
        />
      ) : (
        <button
          onClick={() => { setDropdownOpen((o) => !o); setSearch(""); }}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-gray-300 hover:text-gray-900"
        >
          <span className="max-w-[140px] truncate">{currentLabel()}</span>
          <ChevronDown size={12} className={`shrink-0 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
        </button>
      )}

      {dropdownOpen && (
        <div
          style={{ position: "fixed", top: (anchorRect?.bottom ?? 0) + 4, left: (anchorRect?.left ?? 0), zIndex: 50 }}
          className="w-56 rounded-xl border border-gray-200 bg-white shadow-lg"
        >
          {allOptions.length > 4 && (
            <div className="border-b border-gray-100 px-3 py-2">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search workspaces…"
                className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs focus:border-[#1D345F] focus:outline-none"
              />
            </div>
          )}
          <div className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-gray-400">No results</p>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => { onSelect(opt.id); setDropdownOpen(false); setSearch(""); }}
                  className={`flex w-full items-center px-3 py-2 text-left text-xs hover:bg-gray-50 ${
                    activeWorkspace === opt.id ? "font-semibold text-[#1D345F]" : "text-gray-700"
                  }`}
                >
                  {opt.label}
                  {opt.virtual && (
                    <span className="ml-auto rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-400">
                      {opt.id === "__scheduled__" ? "scheduled" : "default"}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
          <div className="border-t border-gray-100 py-1">
            <button
              onClick={() => { setDropdownOpen(false); onCreate(); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[#1D345F] hover:bg-gray-50"
            >
              <Plus size={12} />
              New workspace
            </button>
          </div>
        </div>
      )}

      {!isScheduledActive && !renaming && (
        <div className="relative">
          <button
            ref={editBtnRef}
            onClick={() => setEditMenuOpen((o) => !o)}
            className="flex items-center rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:border-gray-300 hover:text-gray-700"
            title="Edit workspace"
          >
            <MoreHorizontal size={13} />
          </button>
          {editMenuOpen && (
            <div
              ref={editMenuRef}
              className="absolute left-0 top-full z-50 mt-1 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
            >
              <button
                onClick={startRename}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
              >
                <Pencil size={11} />
                Rename workspace
              </button>
              {!isVirtual && (
                <button
                  onClick={() => { setEditMenuOpen(false); onDelete(activeWorkspace); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-600 hover:bg-gray-50"
                >
                  <Trash2 size={11} />
                  Delete workspace
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Dashboard sortable tab ─────────────────────────────────────────────────────

interface SortableTabProps {
  tab: Tab;
  active: boolean;
  isRenaming: boolean;
  renameValue: string;
  onSelect: () => void;
  onRenameChange: (value: string) => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  onStartRename: () => void;
  onDelete: () => void;
  onMove: () => void;
}

function SortableTab({
  tab,
  active,
  isRenaming,
  renameValue,
  onSelect,
  onRenameChange,
  onCommitRename,
  onCancelRename,
  onStartRename,
  onDelete,
  onMove,
}: SortableTabProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tab.id });
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handle(e: MouseEvent) {
      if (menuBtnRef.current && !menuBtnRef.current.closest("[data-tab-menu]")?.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [menuOpen]);

  function openMenu(e: React.MouseEvent) {
    e.stopPropagation();
    const rect = menuBtnRef.current?.getBoundingClientRect();
    if (rect) setMenuPos({ top: rect.bottom + 4, left: rect.left });
    setMenuOpen((o) => !o);
  }

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onSelect}
      className={`relative shrink-0 border-b-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap cursor-grab select-none active:cursor-grabbing ${
        active
          ? "border-[#1D345F] text-[#1D345F]"
          : "border-transparent text-gray-500 hover:text-gray-800"
      }`}
    >
      <span className="flex items-center gap-1.5">
        {tab.isScheduled && <Calendar size={12} className="shrink-0 opacity-60" />}
        {isRenaming ? (
          <input
            autoFocus
            value={renameValue}
            onChange={(e) => onRenameChange(e.target.value)}
            onBlur={onCommitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") onCommitRename();
              if (e.key === "Escape") onCancelRename();
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            maxLength={80}
            className="w-32 cursor-text rounded border border-[#1D345F] bg-white px-1.5 py-0.5 text-sm text-gray-900 focus:outline-none"
          />
        ) : (
          <span
            onDoubleClick={(e) => { e.stopPropagation(); if (!tab.isScheduled) onStartRename(); }}
            title={tab.isScheduled ? tab.name : "Double-click to rename"}
          >
            {tab.name}
          </span>
        )}
        {active && !isRenaming && (
          <>
            {!tab.isScheduled && (
              <button
                onClick={(e) => { e.stopPropagation(); onStartRename(); }}
                onPointerDown={(e) => e.stopPropagation()}
                className="rounded p-0.5 text-gray-400 hover:text-[#1D345F]"
                title="Rename"
              >
                <Pencil size={11} />
              </button>
            )}
            <span data-tab-menu>
              <button
                ref={menuBtnRef}
                onClick={openMenu}
                onPointerDown={(e) => e.stopPropagation()}
                className="rounded p-0.5 text-gray-400 hover:text-gray-700"
                title="More options"
              >
                <span className="text-xs leading-none">⋯</span>
              </button>
              {menuOpen && (
                <div
                  data-tab-menu
                  style={{ position: "fixed", top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
                  className="w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onMove(); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="flex w-full items-center px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                  >
                    Move to workspace
                  </button>
                  {!tab.isScheduled && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(); }}
                      onPointerDown={(e) => e.stopPropagation()}
                      className="flex w-full items-center px-3 py-2 text-left text-xs text-red-600 hover:bg-gray-50"
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </span>
          </>
        )}
      </span>
    </div>
  );
}
